import {
  DEFAULT_PLAYER_SETTINGS,
  type CatalogSnapshot,
  type ContinueWatchingEntry,
  type FavoriteEntry,
  type PlayerSettings,
  type RecentEntry,
  type SavedServer,
  type StreamServer,
} from '../types';
import { migrateSavedServerToStreamServer } from './migrateServers';
import { normalizeServerUrl } from '../utils/serverFormUtils';

function migrateContinue(items: ContinueWatchingEntry[]): ContinueWatchingEntry[] {
  return items.map((c) => ({
    ...c,
    refId:
      c.refId ??
      (typeof c.key === 'string' && c.key.includes(':') ? c.key.split(':').slice(2).join(':') : c.key),
  }));
}

const PREFIX = 'vorakit_streamplayer_';

const KEYS = {
  serversV1: `${PREFIX}servers_v1`,
  serversV2: `${PREFIX}servers_v2`,
  favorites: `${PREFIX}favorites_v1`,
  recents: `${PREFIX}recents_v1`,
  continueWatching: `${PREFIX}continue_v1`,
  settings: `${PREFIX}settings_v1`,
  lastPlayed: `${PREFIX}last_played_v1`,
  pinnedCategories: `${PREFIX}pinned_categories_v1`,
} as const;

/** `vorakit_streamplayer_catalog_<serverId>` — full library snapshot for instant reload */
const catalogCacheKey = (serverId: string) => `${PREFIX}catalog_${serverId}`;

export interface CatalogCacheEntry {
  catalog: CatalogSnapshot;
  /** Bust cache when URL, credentials, or M3U payload changes (not `updatedAt` — that changes every connect). */
  fingerprint: string;
  savedAt: number;
}

function djb2Hex(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 33) ^ s.charCodeAt(i);
  }
  return (h >>> 0).toString(36);
}

/** Stable key for “same remote library” — used to validate cached catalogs. */
export function catalogConnectionFingerprint(server: StreamServer): string {
  const kind = server.sourceKind ?? 'xtream';
  if (kind === 'm3u_url') {
    return `m3u_url:${server.server.trim()}`;
  }
  if (kind === 'm3u_file') {
    const c = server.m3uFileContent ?? '';
    return `m3u_file:${c.length}:${djb2Hex(c)}`;
  }
  const base = normalizeServerUrl(server.server);
  return `xtream:${base}|${server.username.trim()}|${server.password}`;
}

function safeParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function migrateSettingsActiveId(raw: PlayerSettings): PlayerSettings {
  const base = { ...DEFAULT_PLAYER_SETTINGS, ...raw };
  if (!('activeServerId' in raw)) {
    return { ...base, activeServerId: null };
  }
  return base;
}

/** Load v2 servers; migrate from v1 once if v2 empty. */
export function loadServers(): StreamServer[] {
  const v2 = safeParse<StreamServer[]>(localStorage.getItem(KEYS.serversV2), []);
  if (v2.length > 0) return v2;

  const v1 = safeParse<SavedServer[]>(localStorage.getItem(KEYS.serversV1), []);
  if (v1.length === 0) return [];

  const migrated = v1.map(migrateSavedServerToStreamServer);
  localStorage.setItem(KEYS.serversV2, JSON.stringify(migrated));
  return migrated;
}

export function saveServers(servers: StreamServer[]): void {
  localStorage.setItem(KEYS.serversV2, JSON.stringify(servers));
}

export function loadFavorites(): FavoriteEntry[] {
  return safeParse(localStorage.getItem(KEYS.favorites), []);
}

export function saveFavorites(items: FavoriteEntry[]): void {
  localStorage.setItem(KEYS.favorites, JSON.stringify(items));
}

export function loadRecents(): RecentEntry[] {
  const raw = safeParse<RecentEntry[]>(localStorage.getItem(KEYS.recents), []);
  return raw.map((r) => ({
    ...r,
    refId:
      r.refId ??
      (typeof r.key === 'string' && r.key.includes(':')
        ? r.key.split(':').slice(2).join(':')
        : r.key),
  }));
}

export function saveRecents(items: RecentEntry[]): void {
  localStorage.setItem(KEYS.recents, JSON.stringify(items));
}

export function loadContinueWatching(): ContinueWatchingEntry[] {
  return migrateContinue(safeParse(localStorage.getItem(KEYS.continueWatching), []));
}

export function saveContinueWatching(items: ContinueWatchingEntry[]): void {
  localStorage.setItem(KEYS.continueWatching, JSON.stringify(items));
}

export function loadPlayerSettings(): PlayerSettings {
  return migrateSettingsActiveId({
    ...DEFAULT_PLAYER_SETTINGS,
    ...safeParse<Partial<PlayerSettings>>(localStorage.getItem(KEYS.settings), {}),
  });
}

export function savePlayerSettings(s: PlayerSettings): void {
  localStorage.setItem(KEYS.settings, JSON.stringify(s));
}

export function loadLastPlayed(): RecentEntry | null {
  return safeParse(localStorage.getItem(KEYS.lastPlayed), null);
}

export function saveLastPlayed(entry: RecentEntry | null): void {
  if (!entry) localStorage.removeItem(KEYS.lastPlayed);
  else localStorage.setItem(KEYS.lastPlayed, JSON.stringify(entry));
}

export type PinnedCategory = { sourceId: string; kind: 'live' | 'vod' | 'series'; categoryId: string };

export function loadPinnedCategories(): PinnedCategory[] {
  return safeParse(localStorage.getItem(KEYS.pinnedCategories), []);
}

export function savePinnedCategories(p: PinnedCategory[]): void {
  localStorage.setItem(KEYS.pinnedCategories, JSON.stringify(p));
}

export function loadCatalogCache(serverId: string, fingerprint: string): CatalogSnapshot | null {
  const raw = localStorage.getItem(catalogCacheKey(serverId));
  if (!raw) return null;
  const entry = safeParse<CatalogCacheEntry | null>(raw, null);
  if (!entry?.catalog || entry.fingerprint !== fingerprint) return null;
  if (entry.catalog.sourceId !== serverId) return null;
  return entry.catalog;
}

export function saveCatalogCache(serverId: string, fingerprint: string, catalog: CatalogSnapshot): void {
  try {
    const entry: CatalogCacheEntry = {
      catalog,
      fingerprint,
      savedAt: Date.now(),
    };
    localStorage.setItem(catalogCacheKey(serverId), JSON.stringify(entry));
  } catch (e) {
    console.warn('[Stream Player] Could not save catalog cache (quota or private mode).', e);
  }
}

export function removeCatalogCache(serverId: string): void {
  localStorage.removeItem(catalogCacheKey(serverId));
}

function removeAllCatalogCaches(): void {
  const toRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const k = localStorage.key(i);
    if (k?.startsWith(`${PREFIX}catalog_`)) toRemove.push(k);
  }
  toRemove.forEach((k) => localStorage.removeItem(k));
}

export function clearAllStreamPlayerData(): void {
  Object.values(KEYS).forEach((k) => localStorage.removeItem(k));
  removeAllCatalogCaches();
}

export function exportLocalConfig(): string {
  const blob = {
    servers: loadServers(),
    favorites: loadFavorites(),
    recents: loadRecents(),
    continueWatching: loadContinueWatching(),
    settings: loadPlayerSettings(),
    pinnedCategories: loadPinnedCategories(),
    exportedAt: Date.now(),
    version: 2,
  };
  return JSON.stringify(blob, null, 2);
}

export function importLocalConfig(json: string): { ok: true } | { ok: false; error: string } {
  try {
    const data = JSON.parse(json) as Record<string, unknown>;
    if (data.servers) localStorage.setItem(KEYS.serversV2, JSON.stringify(data.servers));
    if (data.favorites) localStorage.setItem(KEYS.favorites, JSON.stringify(data.favorites));
    if (data.recents) localStorage.setItem(KEYS.recents, JSON.stringify(data.recents));
    if (data.continueWatching) {
      localStorage.setItem(KEYS.continueWatching, JSON.stringify(data.continueWatching));
    }
    if (data.settings) localStorage.setItem(KEYS.settings, JSON.stringify(data.settings));
    if (data.pinnedCategories) {
      localStorage.setItem(KEYS.pinnedCategories, JSON.stringify(data.pinnedCategories));
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'Invalid JSON' };
  }
}
